import { FormsModule } from '@angular/forms';
import { 
  Component, 
  OnInit,
  Input,
  Output, EventEmitter, output,
  signal, 
  computed,
  ContentChildren, 
  QueryList,  
  AfterContentInit, 
  OnDestroy,
  ViewContainerRef,
  viewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { LTPrimeNgModule } from '../../../../third-party/lt-primeng.module';
import { CommonModule } from '@angular/common';
import { Table } from 'primeng/table';
import { SortMeta } from 'primeng/api';
import { GridColumn } from '../../classes/column.interface';
import { GridSettings } from '../../classes/grid-settings.model';
import { GridColumnData } from '../../classes/grid-column-data.class';
import { GridTemplateDirective } from '../../classes/grid-template.directive';
import { LTColumnManagerComponent } from '../lt-column-manager/lt-column-manager.component';
import { LTPNGTableCellComponent } from '../lt-png-table-cell/lt-png-table-cell.component';
import { OverwritePrimeNgService } from '../../services/overwrite-primeng-service.service';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GridSettingsFacade } from '../../classes/grid-settings.facade';
import { Subscription } from 'rxjs';
import { FilterState } from '../../services/table-filter-service.service';
import { LTExportManagerComponent, ExportManagerConfig } from 'src/app/features/grid/components/lt-export-manager/lt-export-manager.component';
import { ExportManagerFacade, ExportResult } from 'src/app/features/grid/classes/export-manager.facade';
import { ColumnPosition, HeaderStructure } from '../../services/grid-column-structure-service.service';
import { LTColumnAggregateComponent } from '../lt-column-aggregate/lt-column-aggregate.component';
import { LTPngTableActionFooterComponent } from '../lt-png-table-action-footer/lt-png-table-action-footer.component';
import { LTPngTableExpansionFooterComponent } from '../lt-png-table-expansion-footer/lt-png-table-expansion-footer.component';
import { LTPngTableSelectionFooterComponent } from '../lt-png-table-selection-footer/lt-png-table-selection-footer.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LTSplitButtonComponent, LTSplitButtonOption } from 'src/app/features/lt-shared-ui/lt-button/components/lt-split-button/lt-split-button.component';
import { ToastService } from 'src/app/services/toast.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { GridCommandButtonDetails } from '../../classes/grid-config.types';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';

@Component({
  selector: 'app-lt-png-table-testing',
  imports: [
    FormsModule,
    CommonModule,
    LTPrimeNgModule,
    LTColumnManagerComponent,
    LTExportManagerComponent,
    LTPNGTableCellComponent,
    LTColumnAggregateComponent,
    LTPngTableActionFooterComponent,
    LTPngTableExpansionFooterComponent,
    LTPngTableSelectionFooterComponent,
    LTSplitButtonComponent,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  standalone: true,
  templateUrl: './lt-png-table-testing.component.html',
  styleUrl: './lt-png-table-testing.component.scss',
  providers: [GridSettingsFacade, OverwritePrimeNgService, ExportManagerFacade, ToastService],
})
export class LtPngTableTestingComponent<T> implements AfterContentInit, OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly toastService = inject(ToastService);
  private readonly overwritePrimeNgService = inject(OverwritePrimeNgService);
  private readonly destroyRef = inject(DestroyRef);
  
  // Table reference
  dt = viewChild<Table>('dt');

  // ContentChildren for custom templates
  @ContentChildren(GridTemplateDirective) templates!: QueryList<GridTemplateDirective>;

  readonly exportContext$ = this.exportManagerFacade.getExportStats();

  //#region Inputs

  //#region GridSettings
  gridSettings = signal<GridSettings>(new GridSettings());
  @Input() set gridSettingsInput(value: GridSettings) {
    this.gridSettings.set(value);
    this.facade.updateSettings(value);

    if (value.multisort && value.sortMode === 'multiple') {
      this.multiSortMeta = value.convertToMultiSortMeta(value.multisort);
    }
    
    this.facade.setLoading(this.gridSettings().loading());
  }

  // multiSort from gridSettings directly makes the sort not able to be updated
  multiSortMeta: SortMeta[] = [];
  //#endregion GridSettings

  //#region showToolbar
  showToolbar = signal<boolean>(true);
  @Input() set showToolbarInput(value: boolean) {
    this.showToolbar.set(value);
  }
  //#endregion showToolbar

  //#endregion Inputs

  //#region Outputs
  @Output() selectionChange = new EventEmitter<readonly any[]>();
  @Output() rowSelect = new EventEmitter<any>();
  @Output() rowUnselect = new EventEmitter<any>();
  readonly columnsChange = output<GridColumnData[]>();
  readonly editClicked = output<any>();
  readonly deleteClicked = output<any>();
  readonly viewClicked = output<any>();
  readonly addClicked = output<boolean>();
  readonly commandClicked = output<{ rowData: any, commandType: string, event: Event }>();
  @Output() filterChange = new EventEmitter<FilterState>();
  @Output() dataFiltered = new EventEmitter<ReadonlyArray<unknown>>();
  //#endregion Outputs

  // Grid state from facade
  readonly gridState = toSignal(this.facade.state$);
  readonly filterState = toSignal(this.facade.filterState$);
  searchValue = signal<string>('');
  
  //#region Computed Signals
  readonly frozenColumns = computed(() => this.gridSettings().getAllFrozenColumns());

  // Selection state helpers for templates
  selectionHandler = computed(() => ({
    getPrimeNGSelectionMode: () => this.facade.getSelectionMode(),
    getSelectionType: () => this.facade.getSelectionType(),
    shouldShowSelectionColumn: () => this.facade.showSelectionColumn(),
    hasMultipleSelection: () => this.facade.hasMultipleSelection(),
    shouldShowSelectAll: () => this.facade.shouldShowSelectAll(),
    getSelectedItems: () => this.facade.getSelectedItems(),
  }));

  // Computed signals derived from facade state
  readonly visibleColumns = computed(() => this.gridState()?.settings.getVisibleColumns() ?? []);
  readonly selectedItems = computed(() => this.gridState()?.selectedItems ?? []);
  readonly expandedRows = computed(() => this.gridState()?.expandedRows ?? {});
  readonly templateMap = computed(() => this.gridState()?.templateMap ?? new Map());
  readonly loading = computed(() => this.gridState()?.loading ?? false);
  readonly globalFilterValue = computed(() => this.gridState()?.globalFilter ?? '');
  readonly filteredData = computed(() => this.gridState()?.filteredData ?? []);
  //filterState = computed(() => this.gridState()?.filterState);
  readonly hasActiveFilters = computed(() => this.filterState()?.isActive ?? false);
  
  // Display data (filtered data when filters are active, otherwise original data)
  displayData = computed(() => {
    const state = this.gridState();
    if (!state) return [];
    
    const filterState = this.filterState();
    return filterState?.isActive ? this.filteredData() : state.settings.data;
  });

  hasDisplayData = computed(() => {
    if (!this.displayData()) return false;
    return this.displayData().length == 0;
  });

  //#endregion Computed Signals

  //#region Export Variables
  readonly showExportManagerButton = computed(() => {
    const displayData = this.displayData();
    return displayData && displayData.length > 0;
  });

  readonly showExportSelected = computed(() => {
    const selectedItems = this.selectedItems();
    return !selectedItems || selectedItems.length === 0;
  });

  exportSplitOptions: LTSplitButtonOption[] = [
    {
      label: 'Export Selected Only',
      icon: 'output',
      click: () => this.quickExportSelected(),
      disabled: this.showExportSelected(),
    },
    {
      label: 'Export Filtered Only',
      icon: 'filter_alt',
      click: () => this.quickExportFiltered(),
    },
    {
      label: 'Export All (Flattened Nested Columns)',
      icon: 'filter_alt',
      click: () => this.quickExportAll(),
      show: this.facade.hasNestedColumns() === true,
    },
    {
      label: 'Export All (Nested Columns)',
      icon: 'filter_alt',
      click: () => this.exportNested(),
      show: this.facade.hasNestedColumns() === true,
    },
    {
      label: 'Export All',
      icon: 'filter_alt',
      click: () => this.handleGeneralExport(),
      show: this.facade.hasNestedColumns() === false,
    },
    {
      label: 'Open Export Manager',
      icon: 'open_in_browser',
      show: false,
      click: () => this.openExportManager(),
      disabled: this.showExportManagerButton(),
    },
  ];
  
  //#endregion Export Variables
  
  constructor(
    private readonly viewContainerRef: ViewContainerRef,
    private readonly router: Router,
    public readonly facade: GridSettingsFacade,
    private readonly exportManagerFacade: ExportManagerFacade,
  ) {
    this.facade.setViewContainerRef(this.viewContainerRef);
  }

  ngOnInit(): void {
    // Initialize with current grid settings
    if (this.gridSettings()) {
      this.facade.updateSettings(this.gridSettings());
    }

    this.exportManagerFacade.initialize(this.facade);

    // Set up debounced search without effects
    this.facade.globalFilter$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        passUntilDestroyed(this.destroyRef),
      )
      .subscribe(globalFilter => {
        if (this.searchValue() !== globalFilter) {
          this.searchValue.set(globalFilter);
        }
      });

    // Subscribe to filter state changes for output events
    this.facade.filterState$
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(filterState => {
        this.filterChange.emit(filterState);
      });
    
    // Subscribe to filtered data changes for output events
    this.facade.filteredData$
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(filteredData => {
        this.dataFiltered.emit(filteredData);
      });
  }

  ngAfterContentInit(): void {
    this.overwritePrimeNgService.overridePrimeNGTableColumnFilter();
    this.updateTemplateMap();
    
    // React to template changes
    this.templates.changes
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateTemplateMap();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.facade.dispose();
    this.exportManagerFacade.dispose();
  }

  //#region Loading and Data Access

  public setLoading(loading: boolean): void {
    this.facade.setLoading(loading);
  }

  public getLoading(): boolean {
    return this.loading();
  }

  public refreshGrid(): void {
    this.facade.refreshData();
  }
  
  getCurrentData(): ReadonlyArray<unknown> {
    return this.displayData();
  }

  getOriginalData(): ReadonlyArray<unknown> {
    return this.gridSettings().data;
  }

  //#endregion Loading and Data Access

  //#region Templates

  private updateTemplateMap(): void {
    this.facade.updateTemplateMap(this.templates);
  }

  //#endregion Templates
  
  //#region Parent Accessibility Functions
  
  // Allows row to be added to grid data from parent component
  addRow(rowData: any): void {
    this.facade.addRow(rowData);
  }

  // Allows row to be removed to grid data from parent component
  removeRow(index: number): void {
    this.facade.removeRow(index);
  }

  // Allows row to be updated in grid data from parent component
  updateRow(index: number, rowData: any): void {
    this.facade.updateRow(index, rowData);
    
  }

  // Allows parent component to toggle column visibility
  toggleColumn(field: string): void {
    this.facade.toggleColumnVisibility(field);
  }
  
  // allows parent component to force a data refresh
  refreshData(): void {
    this.facade.refreshData();
  }
  //#endregion

  //#region Selection

  onRowClick(rowData: any, event: Event): void {
    this.facade.handleRowClick(rowData, event, {
      selectionChange: (items: ReadonlyArray<unknown>) => this.selectionChange.emit(items),
    });
  }

  onSelectionChange(selection: unknown): void {
    this.facade.handleSelectionChange(selection, {
      selectionChange: (items: ReadonlyArray<unknown>) => this.selectionChange.emit(items),
    });
  }

  onRowSelect(event: any): void {
    this.rowSelect.emit(event);
  }

  onRowUnselect(event: any): void {
    this.rowUnselect.emit(event);
  }

  getSelectedItems(): readonly any[] {
    return this.facade.getSelectedItems();
  }

  clearSelection(): void {
    this.facade.clearSelection();

    // const currSelection = this.facade.getSelectedItems();
    // this.gridEvents.selectionChange(currSelection);
  }

  isRowSelected(rowData: any): boolean {
    return this.facade.isRowSelected(rowData);
  }

  toggleRowSelection(rowData: any, selected: boolean): void {
    if (selected) {
      this.facade.selectRow(rowData);
    } else {
      this.facade.deselectRow(rowData);
    }

    // const currSelection = this.facade.getSelectedItems();
    // this.gridEvents.selectionChange(currSelection);
  }

  onSingleSelection(rowData: any): void {
    this.facade.selectRow(rowData);

    // const currSelection = this.facade.getSelectedItems();
    // this.gridEvents.selectionChange(currSelection);
  }

  selectAll(): void {
    this.facade.selectAll();

    // const currSelection = this.facade.getSelectedItems();
    // this.gridEvents.selectionChange(currSelection);
  }

  toggleSelectAll(): void {
    this.facade.toggleSelectAll();

    // const currSelection = this.facade.getSelectedItems();
    // this.gridEvents.selectionChange(currSelection);
  }

  isSelectionDisabled(rowData: any): boolean {
    // TODO: implement custom logic here or delegate to facade
    return false;
  }

  //#endregion Selection

  //#region Expansion

  toggleRowExpansion(rowData: any, event: Event): void {
    //event.stopPropagation();
    this.facade.toggleRowExpansion(rowData);
  }

  isRowExpanded(rowData: any): boolean {
    return this.facade.isRowExpanded(rowData);
  }

  //#endregion Expansion

  //#region CellValue

  getHeaderTooltipText(title: string, tooltip = ''): string {
    return tooltip ? tooltip : title;
  }

  //#endregion

  //#region Filter Management (delegated to facade)
  
  onFilter(event: { filters: Record<string, unknown>; globalFilter?: string }): void {
    // Prevent recursive updates by checking if the global filter actually changed
    if (event.globalFilter !== undefined && event.globalFilter !== this.searchValue()) {
      this.searchValue.set(event.globalFilter);
    }

    this.facade.syncWithPrimeNGFilters(event.filters, event.globalFilter, {
      filterChange: (filterState: FilterState) => this.filterChange.emit(filterState),
      dataFiltered: (filteredData: ReadonlyArray<unknown>) => this.dataFiltered.emit(filteredData),
    });
  }

  setColumnFilter(field: string, value: unknown, operator = 'contains'): void {
    this.facade.setColumnFilter(field, value, operator, {
      filterChange: (filterState: FilterState) => this.filterChange.emit(filterState),
    });
  }

  removeColumnFilter(field: string): void {
    this.facade.removeColumnFilter(field, {
      filterChange: (filterState: FilterState) => this.filterChange.emit(filterState),
    });
  }

  clearAllFilters(): void {
    this.facade.clearAllFilters({
      filterChange: (filterState: FilterState) => this.filterChange.emit(filterState),
    });

    this.searchValue.set('');
    
    // Clear PrimeNG table filters
    const table = this.dt();
    if (table) {
      table.clear();
    }
  }

  applyFilter(value: unknown, field: string, matchMode = 'contains'): void {
    this.setColumnFilter(field, value, matchMode);
    
    // Apply to PrimeNG table for UI sync
    const table = this.dt();
    if (table) {
      table.filter(value, field, matchMode);
    }
  }

  applyFilters(filters: ReadonlyArray<{ value: unknown; field: string; matchMode?: string }>): void {
    const table = this.dt();
    if (table) {
      filters.forEach((filter) => {
        this.setColumnFilter(filter.field, filter.value, filter.matchMode ?? 'contains');
        table.filter(
          filter.value,
          filter.field,
          filter.matchMode ?? 'contains',
        );
      });
    }
  }

  clearFieldFilter(field: string): void {
    this.removeColumnFilter(field);
    
    const table = this.dt();
    if (table) {
      table.filter(null, field, 'contains');
    }
  }

  applyGlobalFilter(value: string): void {
    this.searchValue.set(value);
    this.facade.setGlobalFilter(value);
    
    const table = this.dt();
    if (table) {
      table.filterGlobal(value, 'contains');
    }
  }

  getFilterState(): FilterState | undefined {
    return this.facade.getFilterState();
  }

  getFilteredCount(): number {
    return this.filteredData().length;
  }

  hasFiltersActive(): boolean {
    return this.hasActiveFilters();
  }

  //#endregion Filter Management

  //#region Filter Utility Methods
  
  exportFilterConfig(): Record<string, unknown> {
    return this.facade.exportFilterConfig();
  }

  importFilterConfig(config: Record<string, unknown>): void {
    this.facade.importFilterConfig(config, {
      filterChange: (filterState: FilterState) => this.filterChange.emit(filterState),
    });
    
    // Sync search value
    const filterState = this.facade.getFilterState();
    this.searchValue.set(filterState.globalFilter);
  }

  getFilterSummary(): string {
    return this.facade.getFilterState() ? 
           'Filters active' : // Simplified for component level
           'No filters applied';
  }

  //#endregion Filter Utility Methods

  //#region ColumnManager Dialogs
  public showColumnManager = signal<boolean>(false);

  toggleColumnManager(event: boolean) {
    this.showColumnManager.set(event);
  }
  
  openColumnManager() {
    this.showColumnManager.set(true);
  }

  onColumnsChange(columns: GridColumnData[]): void {
    this.facade.onColumnsChange(columns, {
      columnsChange: (cols: ReadonlyArray<GridColumnData>) => this.columnsChange.emit([...cols]),
    });
  }

  //#endregion

  //#region Search
  public clear(table: Table) {
    table.clear();
    this.searchValue.set('');
    this.clearAllFilters();
  }
  //#endregion

  //#region Export
  export() {
    this.facade.exportToCSV();
    
    // Optional: Show export summary
    if (this.hasActiveFilters()) {
      // const filterSummary = this.facade.getFilterState();
      this.toastService.toastCreate(`Exported ${this.getFilteredCount()} filtered records`, 'Success');
    }
  }

  exportSelected(): void {
    this.facade.exportSelectedToCSV();
  }

  //#region Export Manager Properties
  
  readonly showExportManager = signal<boolean>(false);
  
  readonly exportManagerConfig: ExportManagerConfig = {
    showPreview: true,
    showAdvancedOptions: true,
    allowFilterSaving: true,
    maxPreviewRows: 10,
    defaultFormat: 'csv',
    enableBulkExport: true,
  };

  //#endregion Export Manager Properties

  //#region Export Manager Methods

  openExportManager(): void {
    this.showExportManager.set(true);
  }

  toggleExportManager(visible: boolean): void {
    this.showExportManager.set(visible);
  }

  onExportCompleted(result: ExportResult): void {
    if (result.success) {
      this.toastService.toastCreate(`Exported ${result.recordCount} records to ${result.fileName}`, 'Success', {
          keepAfterRouteChange: true,
      });
    } else {
      // console.error(`Export failed: ${result.error}`);
      this.toastService.toastCreate(`Export Failed: ${result.error}`, 'Danger', {
          keepAfterRouteChange: true,
      });
    }
  }

  onExportCancelled(): void {
    this.toastService.toastCreate('Export cancelled by user', 'Info', {
        keepAfterRouteChange: true,
    });
  }

  handleGeneralExport(): void {
    const hasAggs = this.facade.hasColumnAggregates();
    const hasNestedCols = this.facade.hasNestedColumns();

    if (hasAggs === false && hasNestedCols === false) {
      this.facade.exportToCSV();
    } else {
      this.facade.exportToCSVUpdated(
        null,
        false,
        hasAggs,
        'bottom',
        true,
        false,
      );
    }

  }

  exportNested(){
    this.facade.exportToCSVUpdated(null, false, true, 'bottom', true, false);
  }

  // Quick export method for power users
  async quickExportSelected(): Promise<void> {
    const selectedCount = this.selectedItems().length;
    if (selectedCount === 0) return;

    const options = this.exportManagerFacade.createDefaultOptions('selected');
    const result = await this.exportManagerFacade.exportSelectedPromise(options);
    
    if (result.success) {
      this.toastService.toastCreate(`Quick exported ${result.recordCount} selected items.`, 'Success', {
          keepAfterRouteChange: true,
      });
    }
  }

  async quickExportAll(): Promise<void> {
    const options = this.exportManagerFacade.createDefaultOptions('all');
    const result = await this.exportManagerFacade.exportAllPromise(options);
    if (result.success) {
      this.toastService.toastCreate(`Quick exported all ${result.recordCount} items.`, 'Success', {
          keepAfterRouteChange: true,
      });
    }
  }

  async quickExportFiltered(): Promise<void> {
    const filteredCount = this.filteredData().length;
    if (filteredCount === 0) return;

    const options = this.exportManagerFacade.createDefaultOptions('filtered');
    const result = await this.exportManagerFacade.exportFilteredPromise(options);
    
    if (result.success) {
      this.toastService.toastCreate(`Quick exported ${result.recordCount} filtered items.`, 'Success', {
          keepAfterRouteChange: true,
      });
    }
  }

  //#endregion Export Manager Methods

  //#endregion Export

  //#region Nested Column Support
  getHeaderStructure(): HeaderStructure {
    return this.facade.calculateHeaderStructure();
  }

  getColumnsForHeaderRow(headerRow: ReadonlyArray<GridColumn | GridColumnData>, position: ColumnPosition): ReadonlyArray<GridColumn | GridColumnData> {
    return this.facade.getColumnsForHeaderRow(headerRow, position);
  }

  getLeafColumns(position: ColumnPosition): ReadonlyArray<GridColumn | GridColumnData> {
    return this.facade.getLeafColumnsByPosition(position);
  }

  getColumnSpan(column: GridColumn): number {
    return column.colspan ?? this.facade.calculateColspan(column);
  }

  getRowSpan(column: GridColumn, maxDepth: number, currentRowIndex: number): number {
    return this.facade.calculateRowspan(column, maxDepth, currentRowIndex);
  }

  //#endregion Nested Column Support

  //#region Command Button Handling
  onCommandClicked(rowData: any, commandType: string, event: Event, customHandler?: (rowData: any, commandType: string) => void): void {
    if (customHandler) {
      customHandler(rowData, commandType);
      return;
    }
    this.commandClicked.emit({ rowData, commandType, event });

    if (commandType === 'edit') {
      this.editClicked.emit(rowData);
    }
    else if (commandType === 'delete') {
      this.deleteClicked.emit(rowData);
    }
    else if (commandType === 'view') {
      this.viewClicked.emit(rowData);
    }
    else if (commandType === 'add') {
      this.addClicked.emit(true);
    }
  }

  onAddClicked(): void {
    this.addClicked.emit(true);
  }

  isButtonVisible(button: GridCommandButtonDetails, rowData: any = null): boolean {
    if (button.visible === undefined) return true;
    return button.visible(rowData);
  }

  isButtonDisabled(button: GridCommandButtonDetails, rowData: any = null): boolean {
    if (button.disabled === undefined) return false;
    return button.disabled(rowData);
  }

  shouldShowAddButton(): boolean {
    return this.gridSettings().showAddButton === true;
  }
  //#endregion
}
